/**
 * The terms/privacy consent gate.
 *
 * WHY IT EXISTS: `terms_accepted_at` was being stamped by the server on every registration while
 * the UI asked for nothing (found while drafting docs/legal, 2026-08-01) — a stored consent
 * timestamp for an event the user was never shown. Worse, the 1085 accounts imported from v1 never
 * had a consent event at all: they predate these documents entirely.
 *
 * WHAT IT DOES (owner decision, 2026-08-01): at the next sign-in, anyone whose recorded consent is
 * missing or older than the CURRENT terms version gets a blocking dialog with links to both
 * documents and one action — agree. Declining means not using the service, which is what the Terms
 * themselves say; the dialog therefore offers "agree" or sign-out, not a dismiss button.
 *
 * RE-CONSENT ON CHANGE: bump `TERMS_EFFECTIVE_DATE` whenever the documents change materially. Any
 * acceptance recorded BEFORE that instant stops counting and the gate re-appears — which is the
 * mechanism the Terms' own "we may update these terms" clause relies on.
 */
import { getAccountState, acceptTerms } from "../api/account-api";
import { t } from "../i18n";
import { logout } from "../auth/auth-service";

/**
 * The instant the current documents took effect. Acceptances older than this are stale and the gate
 * asks again. ISO-8601, UTC. Bump this — and only this — when the documents change materially.
 *
 * MUST NOT BE IN THE FUTURE. Registration records consent the moment the user accepts the notice on
 * the signup form; if this constant is later than that, a user who just consented is immediately
 * asked again — and every e2e spec (which registers a throwaway user and then drives the UI) ends
 * up with a blocking modal over the page. That is exactly what happened when this shipped as
 * 2026-08-01 while it was still 2026-07-31 UTC: clicks were intercepted, specs timed out and
 * retried, and the pipeline took 35 minutes instead of 15.
 */
// Bumped 2026-10-11 (SCRUM-731) because the change IS material: the Terms gained Section 5 —
// offers between participants are messages, not contractual offers, and we are not a party to
// anyone's deal — and the Privacy Policy gained the shows, offers, approval history, show links and
// calendar feed that did not exist when the previous version was accepted. Both documents promise
// in their own words (Terms 14, Privacy 10) that we ask again on a material change; leaving this at
// July would publish new Terms that nobody has accepted while the document claims otherwise.
//
// CONSEQUENCE, and it is visible to EVERY existing account: the notice is shown again on their next
// visit. That is the point of the constant, but it means this must not ship before the documents it
// refers to are the ones actually served.
export const TERMS_EFFECTIVE_DATE = "2026-10-11T00:00:00Z";

let shown = false;

function isCurrent(acceptedAt: string | null): boolean {
  if (!acceptedAt) return false;
  const accepted = new Date(acceptedAt).getTime();
  if (Number.isNaN(accepted)) return false;
  return accepted >= new Date(TERMS_EFFECTIVE_DATE).getTime();
}

function render(): HTMLElement {
  const overlay = document.createElement("div");
  overlay.className = "consent-overlay";
  overlay.id = "consent-overlay";
  // Modal semantics: this is a blocking decision, so it must trap attention rather than sit behind
  // the app. There is deliberately no close affordance and no click-outside dismissal.
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-labelledby", "consent-title");
  overlay.innerHTML = `
    <div class="consent-dialog">
      <h2 id="consent-title">${t("consent.title")}</h2>
      <p>${t("consent.body", {
        termsLink: `<a href="/terms" target="_blank" rel="noopener">${t("login.termsLink")}</a>`,
        privacyLink: `<a href="/privacy" target="_blank" rel="noopener">${t("login.privacyLink")}</a>`,
      })}</p>
      <p class="consent-note">${t("consent.changeNote")}</p>
      <p class="consent-error" id="consent-error" hidden></p>
      <div class="consent-actions">
        <button type="button" id="consent-decline" class="btn-secondary">${t("consent.decline")}</button>
        <button type="button" id="consent-agree">${t("consent.agree")}</button>
      </div>
    </div>
  `;
  return overlay;
}

/**
 * Shows the gate if this account's consent is missing or stale. Safe to call on every mount: it
 * no-ops once shown, and a failed profile read never blocks the app (the gate is a legal
 * requirement, not a reason to make the product unusable when the API hiccups — it will ask again
 * on the next load).
 */
export async function ensureTermsAccepted(): Promise<void> {
  if (shown || document.getElementById("consent-overlay")) return;

  let acceptedAt: string | null;
  try {
    acceptedAt = (await getAccountState()).termsAcceptedAt;
  } catch {
    return;
  }
  if (isCurrent(acceptedAt)) return;

  shown = true;
  const overlay = render();
  document.body.appendChild(overlay);

  const agree = overlay.querySelector<HTMLButtonElement>("#consent-agree")!;
  const decline = overlay.querySelector<HTMLButtonElement>("#consent-decline")!;
  const error = overlay.querySelector<HTMLElement>("#consent-error")!;

  agree.addEventListener("click", () => {
    agree.disabled = true;
    error.hidden = true;
    void acceptTerms()
      .then(() => {
        overlay.remove();
      })
      .catch(() => {
        // Never record consent the server did not store: if the write failed, the user has NOT
        // consented as far as the record goes, so keep asking.
        agree.disabled = false;
        error.textContent = t("consent.saveFailed");
        error.hidden = false;
      });
  });

  decline.addEventListener("click", () => {
    overlay.remove();
    shown = false;
    logout();
  });

  agree.focus();
}

/** Test seam: lets a fresh page/session ask again after a sign-out within the same tab. */
export function resetConsentGate(): void {
  shown = false;
}
